<template>
  <!-- d-print-none: join chrome (exit bar, logo, recruit name) is excluded from
       browser print so a printed join confirmation shows only the receipt content. -->
  <div class="d-print-none">
    <!-- Pink exit bar -->
    <div class="ida-header d-flex align-center">
      <VContainer class="d-flex align-center justify-space-between">
        <SBtn ref="exitButton" link-medium color="white" class="text-uppercase" data-testid="ida-btn-exit" @click="onExitClick">
          {{ $t('ida.btn.exit') }}
        </SBtn>

        <!-- Mobile: JOIN STAMPIN' UP! tag -->
        <VChip
          class="ida-join-tag d-md-none mr-n2 font12 font-weight-medium text-uppercase text-primary"
          variant="flat"
          color="backgroundPage"
          data-testid="ida-join-tag-mobile"
        >
          {{ $t('ida.label.demoApplication') }}
        </VChip>
      </VContainer>
    </div>

    <!-- The agent exit can fail with the agent still impersonating (clear-impersonation
         refused): the exit does not navigate then, so say so instead of leaving the
         pressed button silent. AB#64477 -->
    <VContainer v-if="exitFailed && joinStore.error" class="pt-2 pb-0">
      <ErrorContainer :message="$ts(joinStore.error)" data-testid="ida-exit-error" />
    </VContainer>

    <!-- Logo / recruit name bar -->
    <VContainer class="ida-logo-bar">
      <div class="d-flex align-center justify-space-between">
        <!-- Mobile: typeset logo -->
        <component
          :is="isConfirmation ? NuxtLink : 'span'"
          v-bind="isConfirmation ? { to: '/' } : {}"
          class="ida-logo d-md-none"
        >
          <img
            src="@/assets/images/SU_LOGO_typeset.png"
            alt="typeset logo"
            height="30"
          >
        </component>
        <!-- Desktop: boxed logo -->
        <component
          :is="isConfirmation ? NuxtLink : 'span'"
          v-bind="isConfirmation ? { to: '/' } : {}"
          class="ida-logo d-none d-md-block"
        >
          <img
            :src="logoSrc"
            alt="Stampin' Up!"
            height="64"
            class="ida-logo-img"
          >
        </component>

        <div class="d-flex align-center ga-3 ml-auto">
          <span v-if="recruitName" class="font16" data-testid="ida-recruit-name">
            {{ recruitName }}
          </span>
          <VChip
            class="ida-join-tag d-none d-md-inline-flex font12 font-weight-medium text-uppercase text-primary"
            size="small"
            variant="flat"
            color="backgroundPage"
            data-testid="ida-join-tag"
          >
            {{ $t('ida.label.demoApplication') }}
          </VChip>
        </div>
      </div>
    </VContainer>

    <VDivider class="ma-0" />

    <JoinExitConfirmModal
      :show-dialog="showExitConfirm"
      @update:show-dialog="showExitConfirm = $event"
      @exit="confirmExit"
      @cancel="showExitConfirm = false"
    />
  </div>
</template>

<script lang="ts" setup>
import ErrorContainer from '~/components/base/ErrorContainer.vue'
import JoinExitConfirmModal from '~/components/join/JoinExitConfirmModal.vue'
import { useJoinStore } from '~/stores/join'
import { mediaAssetUrl } from '~/utils/format/mediaAssets'
import { JOIN_CONFIRMATION } from '~/utils/joinRoutes'

const route = useRoute()
const { redirectWithReload } = useRedirect()
const { isAgentRecruit } = useSAuthState()
const joinStore = useJoinStore()

// Same stored key and same casing trap as HeaderLogo (ADO 62986).
const logoSrc = computed(() => mediaAssetUrl('application_assets/SU_Logo.png'))

const NuxtLink = resolveComponent('NuxtLink')
const showExitConfirm = ref(false)
const exitFailed = ref(false)
const exitButton = useTemplateRef('exitButton')

const isConfirmation = computed(() => route.path.includes(JOIN_CONFIRMATION))

const recruitName = computed(() => {
  const s = joinStore.session
  if (s.firstName) {
    return `${s.firstName} ${s.lastName || ''}`.trim()
  }
  return ''
})

const onExitClick = () => {
  // On the confirmation page the join is complete, so there's no in-progress data
  // to lose — skip the "are you sure?" dialog and exit straight to the homepage.
  if (isConfirmation.value) {
    confirmExit()
    return
  }
  showExitConfirm.value = true
}

const confirmExit = async () => {
  showExitConfirm.value = false
  exitFailed.value = false
  // An agent's test recruit goes back to the agent session rather than signing out. AB#64477
  if (isAgentRecruit.value) {
    // Navigate only once the agent session is actually back: leaveAsAgent returns false with
    // the recruit state intact when the clear failed, so Exit can be pressed again.
    if (await joinStore.leaveAsAgent()) {
      redirectWithReload('/')
    } else {
      // leaveAsAgent sets the store error before it returns false. The confirm modal has
      // closed, so focus goes back to Exit rather than the page body; the error row is an
      // alert and announces itself.
      exitFailed.value = true
      exitButton.value?.$el?.focus()
    }
    return
  }
  await joinStore.clearSession()
  // Full document load, not router.push — see redirectWithReload (AB#63360).
  // clearSession() has already dropped the isRecruit cookie by this point, so
  // recruit-guard will not intercept and force a reload either. This is the
  // default exit on every join step, the most-travelled of the three.
  redirectWithReload('/')
}
</script>

<style lang="scss" scoped>
.ida-header {
  height: 48px;
  z-index: 12;
  background-color: rgb(var(--v-theme-userBackgroundColor));
}

.ida-logo-bar {
  padding-top: 12px;
  padding-bottom: 12px;
}

.ida-logo {
  flex-shrink: 0;
  max-width: 160px;
  line-height: 0;

  img {
    display: block;
    max-width: 100%;
    height: auto;
  }
}

.ida-logo-img {
  object-fit: contain;
  max-height: 64px;
  width: auto;
}

.ida-join-tag {
  height: auto;
  padding-top: 2px;
  padding-bottom: 2px;
}
</style>
